import React, { useMemo } from 'react';
import {
  Box,
  ExpandableSection,
  Steps,
  StepsProps
} from '@amzn/awsui-components-console';
import './ThinkingPanel.scss';

export interface ThinkingStep {
  tool_name: string;
  status: 'in_progress' | 'complete' | 'error';
  result_summary?: string;
  details?: string[];
  error_message?: string;
}

export interface ThinkingPanelProps {
  steps: ThinkingStep[];
  expandable?: boolean;
  duration?: number;
  isStreaming?: boolean;
}

/**
 * ThinkingPanel - Shows agent's reasoning and tool calls in real time
 * 
 * Displays inline in chat while agent is working, showing:
 * - Tool calls being executed
 * - Progress indicators
 * - Results and details
 * - Expandable/collapsible view
 * 
 * Uses Cloudscape Steps component for consistent UI
 */
export const ThinkingPanel: React.FC<ThinkingPanelProps> = ({
  steps,
  expandable = true,
  duration,
  isStreaming = false
}) => {
  // Convert ThinkingStep[] to Cloudscape Steps format
  const cloudscapeSteps: StepsProps['steps'] = useMemo(() => {
    const stepsAccumulator: StepsProps.Step[] = [];

    steps.forEach((step) => {
      // Map status to Cloudscape status
      let status: StepsProps.Step['status'] = 'info';
      if (step.status === 'complete') {
        status = 'success';
      } else if (step.status === 'in_progress') {
        status = 'loading';
      } else if (step.status === 'error') {
        status = 'error';
      }

      // Build details content
      const detailsContent: React.ReactNode[] = [];
      
      if (step.result_summary) {
        detailsContent.push(
          <Box key="summary" color="text-body-secondary" padding={{ bottom: 'xs' }}>
            {step.result_summary}
          </Box>
        );
      }

      if (step.details && step.details.length > 0) {
        detailsContent.push(
          <Box key="details" color="text-body-secondary" fontSize="body-s">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {step.details.map((detail, idx) => (
                <li key={idx}>{detail}</li>
              ))}
            </ul>
          </Box>
        );
      }

      if (step.error_message) {
        detailsContent.push(
          <Box key="error" color="text-status-error" padding={{ top: 'xs' }}>
            Error: {step.error_message}
          </Box>
        );
      }

      stepsAccumulator.push({
        header: step.tool_name,
        status,
        details: detailsContent.length > 0 ? (
          <Box padding="s">{detailsContent}</Box>
        ) : undefined
      });
    });

    // Add final status step
    const hasErrors = steps.some(s => s.status === 'error');
    const allComplete = steps.length > 0 && steps.every(s => s.status === 'complete');
    
    if (hasErrors) {
      stepsAccumulator.push({
        header: 'Completed with errors',
        status: 'error'
      });
    } else if (isStreaming || steps.some(s => s.status === 'in_progress')) {
      stepsAccumulator.push({
        header: 'Generating response',
        status: 'loading'
      });
    } else if (allComplete) {
      stepsAccumulator.push({
        header: 'Successfully completed',
        status: 'success',
        details: duration !== undefined ? (
          <Box color="text-body-secondary" padding="s">
            Duration: {duration.toFixed(1)}s
          </Box>
        ) : undefined
      });
    }

    return stepsAccumulator;
  }, [steps, isStreaming, duration]);

  const hasInProgressStep = steps.some(step => step.status === 'in_progress');
  const isActive = isStreaming || hasInProgressStep;
  const allDone = !isActive && steps.length > 0;

  const headerLabel = isActive
    ? 'ADDAPT  intelligence is thinking...'
    : `Used ${steps.length} tool${steps.length !== 1 ? 's' : ''}`;

  const headerContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {isActive && (
        <span className="thinking-panel__spinner">◉</span>
      )}
      {allDone && (
        <span style={{ color: '#0972d3' }}>◉</span>
      )}
      <span>{headerLabel}</span>
    </div>
  );

  return (
    <div className="thinking-panel">
      <ExpandableSection
        variant="footer"
        headerText={headerContent}
        defaultExpanded={expandable}
      >
        <Steps steps={cloudscapeSteps} />
      </ExpandableSection>
    </div>
  );
};

export default ThinkingPanel;
